@file:Suppress(
    "RETURN_VALUE_NOT_USED",
    "RETURN_VALUE_NOT_USED_COERCION",
)

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Suppress
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import org.example.willfreightnew.components.layouts.MarkdownLayout as _org_example_willfreightnew_components_layouts_MarkdownLayout
import org.example.willfreightnew.components.layouts.PageLayout as _org_example_willfreightnew_components_layouts_PageLayout
import org.example.willfreightnew.pages.AboutPage as _org_example_willfreightnew_pages_AboutPage
import org.example.willfreightnew.pages.HomePage as _org_example_willfreightnew_pages_HomePage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "Will Freight Services"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "org.example.willfreightnew.components.layouts.MarkdownLayout",
            parentLayoutId = "org.example.willfreightnew.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                org.example.willfreightnew.components.layouts.initMarkdownLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _org_example_willfreightnew_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "org.example.willfreightnew.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _org_example_willfreightnew_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "org.example.willfreightnew.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                org.example.willfreightnew.pages.initHomePage(ctx)
            },
        ) { pageCtx -> 
            _org_example_willfreightnew_pages_HomePage()
        }
        ctx.router.register(
            "/about",
            layoutId = "org.example.willfreightnew.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                org.example.willfreightnew.pages.initAboutPage(ctx)
            },
        ) { pageCtx -> 
            _org_example_willfreightnew_pages_AboutPage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("headline-text", org.example.willfreightnew.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", org.example.willfreightnew.SubheadlineTextStyle)
        ctx.theme.registerStyle("header-link", org.example.willfreightnew.HeaderLinkStyle)
        ctx.theme.registerStyle("header-underline", org.example.willfreightnew.HeaderUnderlineStyle)
        ctx.theme.registerStyle("service-card", org.example.willfreightnew.ServiceCardStyle)
        ctx.theme.registerStyle("contact-card", org.example.willfreightnew.ContactCardStyle)
        ctx.theme.registerStyle("nav-link", org.example.willfreightnew.NavLinkStyle)
        ctx.theme.registerStyle("back-to-top", org.example.willfreightnew.BackToTopStyle)
        ctx.theme.registerStyle("hero-container", org.example.willfreightnew.pages.HeroContainerStyle)
        ctx.theme.registerStyle("markdown", org.example.willfreightnew.components.layouts.MarkdownStyle)
        ctx.theme.registerStyle("page-content", org.example.willfreightnew.components.layouts.PageContentStyle)
        ctx.theme.registerStyle("footer", org.example.willfreightnew.components.sections.FooterStyle)
        ctx.theme.registerStyle("nav-header", org.example.willfreightnew.components.sections.NavHeaderStyle)
        ctx.theme.registerVariant("-circle", org.example.willfreightnew.CircleButtonVariant
        )
        ctx.theme.registerVariant("-uncolored", org.example.willfreightnew.UncoloredButtonVariant
        )
        ctx.theme.registerKeyframes("side-menu-slide-in", org.example.willfreightnew.components.sections.SideMenuSlideInAnim)
        org.example.willfreightnew.initSiteStyles(ctx)
        org.example.willfreightnew.initColorMode(ctx)
        org.example.willfreightnew.initStyles(ctx)
        org.example.willfreightnew.initTheme(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        org.example.willfreightnew.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
